/**
 * ============================================================
 * TRESTLE™ Workspace API
 * ------------------------------------------------------------
 * Communication Layer between Shopify Workspace
 * and Google Apps Script Backend.
 *
 * Brand        : Diafemme®
 * Module       : TRESTLE™ Workspace
 * File         : trestle-workspace-api.js
 *
 * Version      : 3.0
 * Status       : PRODUCTION
 *
 * Responsibilities
 * ------------------------------------------------------------
 * • Google Apps Script Communication
 * • API Requests
 * • API Responses
 * • Error Handling
 * • Dashboard Loading
 * • Daily Intentions
 * • Workspace
 * • Health Logs
 * • Reports
 * ============================================================
 */

"use strict";

const TrestleApi = (() => {

    /*==========================================================
    CONFIG
    ==========================================================*/

    const config = TrestleConfig;

    let initialized = false;

    /*==========================================================
    INITIALIZE
    ==========================================================*/

    function initialize() {

        if (initialized) {

            return;

        }

        initialized = true;

        console.log(

            "TRESTLE™ Workspace API v3.0 Initialized."

        );

    }

    /*==========================================================
    CUSTOMER
    ==========================================================*/

    function getCustomer() {

        return window.TrestleCustomer || {

            customerId: "",

            firstName: "",

            lastName: "",

            email: ""

        };

    }

    /*==========================================================
    REQUEST
    ==========================================================*/

    async function request(action, data = {}) {

        return await post({

            action,

            data

        });

    }

    /*==========================================================
    POST
    ==========================================================*/

    async function post(payload) {

    initialize();

    console.log("POST URL:", config.API.BASE_URL);
    console.log("POST PAYLOAD:", payload);

    try {

        const response = await fetchWithTimeout(

            config.API.BASE_URL,

            {

                method: "POST",

                body: new URLSearchParams({

                    payload: JSON.stringify(payload)

                })

            }

        );

        console.log("RAW RESPONSE:", response);

        return await handleResponse(response);

    }

    catch(error){

        console.error("POST FAILED:", error);

        throw error;

    }

}

    /*==========================================================
    FETCH WITH TIMEOUT
    ==========================================================*/

async function fetchWithTimeout(url, options) {

    console.log("Starting Fetch");

    const controller = new AbortController();

    const timeout = setTimeout(() => {

        console.log("TIMEOUT REACHED");

        controller.abort();

    }, config.API.TIMEOUT);

    try {

        const response = await fetch(url, {
    ...options,
    signal: controller.signal
});

console.log("STATUS :", response.status);
console.log("OK :", response.ok);
console.log("URL :", response.url);
console.log("REDIRECTED :", response.redirected);
console.log("TYPE :", response.type);

        console.log("FETCH COMPLETED");

        clearTimeout(timeout);

        return response;

    }

    catch (error) {

        console.log("FETCH ERROR:", error.name);

        clearTimeout(timeout);

        throw error;

    }

}

    /*==========================================================
    HANDLE RESPONSE
    ==========================================================*/

    async function handleResponse(response) {

    console.log("FINAL STATUS :", response.status);
    console.log("FINAL URL :", response.url);

    const text = await response.text();

    console.log("RESPONSE BODY :", text);

    return JSON.parse(text);

}

    /*==========================================================
    HANDLE ERROR
    ==========================================================*/

    function handleError(error) {

        console.error(

            "TRESTLE API",

            error

        );

        return {

            success: false,

            message:

                error.message,

            data: null

        };

    }
    /*==========================================================
    LOGIN
    ==========================================================*/

    async function login(userData) {

        return await request(

            config.ACTIONS.LOGIN,

            userData

        );

    }

    /*==========================================================
    LOAD DASHBOARD
    ==========================================================*/

    async function loadDashboard() {

        const customer =

            getCustomer();

        return await request(

            config.ACTIONS.LOAD_DASHBOARD,

            {

                shopifyCustomerId:

                    customer.customerId,

                firstName:

                    customer.firstName,

                lastName:

                    customer.lastName,

                email:

                    customer.email

            }

        );

    }

    /*==========================================================
    LOAD DAILY INTENTION
    ==========================================================*/

    async function loadDailyIntention(

        userId,

        intentionDate

    ) {

        return await request(

            config.ACTIONS.LOAD_DAILY_INTENTION,

            {

                userId:

                    userId,

                intentionDate:

                    intentionDate

            }

        );

    }

    /*==========================================================
    SAVE DAILY INTENTION
    ==========================================================*/

    async function saveDailyIntention(data) {

        return await request(

            config.ACTIONS.CREATE_DAILY_INTENTION,

            data

        );

    }
    /*==========================================================
    LOAD WORKSPACE
    ==========================================================*/

    async function loadWorkspace(workspaceId) {

        return await request(

            config.ACTIONS.LOAD_WORKSPACE,

            {

                workspaceId:

                    workspaceId

            }

        );

    }

    /*==========================================================
    CREATE WORKSPACE
    ==========================================================*/

    async function createWorkspace(workspace) {

        return await request(

            config.ACTIONS.CREATE_WORKSPACE,

            workspace

        );

    }

    /*==========================================================
    LOAD HEALTH LOG
    ==========================================================*/

    async function loadHealthLog(logId) {

        return await request(

            config.ACTIONS.LOAD_HEALTH_LOG,

            {

                logId:

                    logId

            }

        );

    }
            /*==========================================================
        LOAD HEALTH PROFILE
        ==========================================================*/

        async function loadHealthProfile(userId) {

            return await request(

                config.ACTIONS.LOAD_HEALTH_PROFILE,

                {

                    userId:

                        userId

                }

            );

        }

            /*==========================================================
            SAVE HEALTH PROFILE
            ==========================================================*/

            async function saveHealthProfile(profile) {

                return await request(

                    config.ACTIONS.SAVE_HEALTH_PROFILE,

                    profile

                );

            }
            /*==========================================================
            SAVE USER PROFILE
            ==========================================================*/

            async function saveUserProfile(profile) {

                return await request(

                    config.ACTIONS.SAVE_USER_PROFILE,

                    profile

                );

            }

    /*==========================================================
    CREATE HEALTH LOG
    ==========================================================*/

    async function createHealthLog(log) {

        return await request(

            config.ACTIONS.CREATE_HEALTH_LOG,

            log

        );

    }

    /*==========================================================
    LOAD REPORT
    ==========================================================*/

    async function loadReport(reportId) {

        return await request(

            config.ACTIONS.LOAD_REPORT,

            {

                reportId:

                    reportId

            }

        );

    }

    /*==========================================================
    CREATE REPORT
    ==========================================================*/

    async function createReport(report) {

        return await request(

            config.ACTIONS.CREATE_REPORT,

            report

        );

    }
    /*==========================================================
    PUBLIC API
    ==========================================================*/

    return {

    login,

    loadDashboard,

    loadDailyIntention,

    saveDailyIntention,

    loadWorkspace,

    createWorkspace,

    loadHealthLog,

    createHealthLog,

    loadHealthProfile,

    saveHealthProfile,

    saveUserProfile,

    loadReport,

    createReport

};

})();

/*==========================================================
GLOBAL EXPORT
==========================================================*/

window.TrestleApi = TrestleApi;